import { useGlobalErrStore } from '../../store/errorStore';
import { ErrorCode, convertStringToErrorCode } from '../../api/errorCode';
import { useLoadingStore } from '../../store/loadingStore';
import { ServiceType } from '../../enum/EnumRepository';
import { UpdateBillboardReq, UpdateMemberInfoReq, UpdateProjectReq, UpdateSeoReq, UpdateTextBlockReq, UpdateUploadFileReq, UpdateWorkDetailReq, UpdateWorkReq } from '../../dto/ReqDtoRepository';
import { base64ToFileWithMime, makeOriginFilename, makePrefix } from '../../aws/awsHelper';
import { uploadFileWithPreSignedUrl } from '../../api/preSignedUrlApi';
import { usePreSigned } from './usePreSigned';
import { getConfig } from '../../env/envManager';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { FILE_MAX_SIZE } from '../../constants/limits';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';

type UpdateReqType = UpdateBillboardReq | UpdateMemberInfoReq | UpdateProjectReq | UpdateWorkReq | UpdateWorkDetailReq;
type UpdatePEType = { id: string, updateUploadFileReq: UpdateUploadFileReq, workId?: string, formationId?: string };

interface UseImageResult {
  uploadImage: (serviceType: ServiceType, data: UpdateReqType, imageFile?: File) => Promise<UpdateReqType | null>;
  uploadPEImage: <T extends UpdatePEType>(serviceType: ServiceType, data: T, projectId?: string) => Promise<T | null>;
  uploadSeoImage: (data: UpdateSeoReq) => Promise<UpdateSeoReq | null>;
  uploadTextBlockImage: (textId: string, data: UpdateTextBlockReq) => Promise<UpdateTextBlockReq | null>;
}
const config = getConfig();


export const useImage = (): UseImageResult => {
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { addLoading, popLoading } = useLoadingStore();
  const { setManagedErr, clearErr } = useGlobalErrStore();
  const { getPreSignedUrl } = usePreSigned();


  const uploadToS3 = async (file: File, aui: string, serviceType: ServiceType, identifier: string[]): Promise<string> => {
    const prefix = makePrefix(serviceType, identifier);
    const bucketName = config.s3BucketName;

    //   // Upload original file
    const originalFileKey = `uploads/${aui}/${prefix}/${makeOriginFilename(file.type)}`;

    // 여기서 pre-signed url 발급
    const preSignedUrl = await getPreSignedUrl({ fileName: originalFileKey, fileType: file.type, fileSize: file.size });

    // 여기서 pre-signed url로 파일 업로드
    if (preSignedUrl) {
      const uploaded = await runCurrentArchiveRequest(
        aui,
        () => uploadFileWithPreSignedUrl(preSignedUrl, file),
        () => true,
      );
      if (!uploaded) return '';
    }else{
      return '';
    }

    return `https://${bucketName}.s3.amazonaws.com/${originalFileKey}`;
  }

  //이미지 크기 확인
  const sizeCheckBig = (file: File): boolean => {
    if (file.size > FILE_MAX_SIZE) {
      return false;
    }
    return true;
  }

  //ProjectElement (Work, Detail, Document, TextBlock) 사용
  const uploadFileWithLocalUrlPE = async <T extends UpdatePEType>(
    serviceType: ServiceType,
    prevData: T,
    projectId?: string
  ): Promise<T | null> => {
    if (!pageOwner) return null;
    addLoading();
    clearErr();
    const { aui } = pageOwner;
    try {
      const localImageUrl = prevData.updateUploadFileReq.originUrl;
      const file = base64ToFileWithMime(localImageUrl);
      if (!sizeCheckBig(file)) {
        throw new Error(ErrorCode.BFE);
      }
      let originUrl: string;
      switch (serviceType) {
        case ServiceType.WORK:
          originUrl = await uploadToS3(file, aui, serviceType, [prevData.id]);
          break;
        case ServiceType.DETAIL:
          originUrl = await uploadToS3(file, aui, serviceType, [prevData.workId!, prevData.id]);
          break;
        case ServiceType.DOCUMENT:
          originUrl = await uploadToS3(file, aui, serviceType, [projectId!, prevData.id]);
          break;
        case ServiceType.PDF:
          originUrl = await uploadToS3(file, aui, serviceType, [projectId!, prevData.id]);
          break;
        case ServiceType.TEXT_BLOCK:
          originUrl = await uploadToS3(file, aui, serviceType, [projectId!, prevData.id]);
          break;
        case ServiceType.FORMATION:
          originUrl = await uploadToS3(file, aui, serviceType, [projectId!, prevData.formationId!, prevData.id]);
          break;
        default:
          throw new Error('Unsupported service type');
      }
      if (originUrl === '') {
        return null;
      }
      return {
        ...prevData,
        updateUploadFileReq: {
          ...prevData.updateUploadFileReq,
          originUrl
        }
      };
    } catch (err) {
      const errCode = err instanceof Error ? err.message as ErrorCode : ErrorCode.AWS;
      setManagedErr({
        errCode: convertStringToErrorCode(errCode)
      });
      return null;
    } finally {
      popLoading();
    }
  }


  //Billboard, Work, Detail(workId 필요), Project, MemberInfo 사용
  const uploadFileWithLocalUrl = async <T extends UpdateReqType & { id: string, workId?: string }>(
    serviceType: ServiceType,
    prevData: T,
    imageFile?: File,
  ): Promise<T | null> => {
    if (!pageOwner) return null;
    addLoading();
    clearErr();
    const { aui } = pageOwner;
    try {
      const localImageUrl = prevData.updateUploadFileReq?.originUrl;
      if (!imageFile && !localImageUrl) {
        throw new Error('RVN');
      }
      const file = imageFile ?? base64ToFileWithMime(localImageUrl!);
      if (!sizeCheckBig(file)) {
        throw new Error(ErrorCode.BFE);
      }

      let extraData: string[] = [];
      switch (serviceType) {
        case ServiceType.BILLBOARD:
          extraData = [prevData.id];
          break;
        case ServiceType.WORK:
          extraData = [prevData.id];
          break;
        case ServiceType.DETAIL:
          extraData = [prevData.workId!, prevData.id];
          break;
        case ServiceType.PROJECT:
          extraData = [prevData.id];
          break;
        case ServiceType.MEMBER_INFO:
          extraData = [prevData.id];
          break;
        default:
          throw new Error('Unsupported service type');
      }

      const originUrl = await uploadToS3(file, aui, serviceType, extraData);
      if (originUrl === '') {
        return null;
      }
      return {
        ...prevData,
        updateUploadFileReq: {
          ...prevData.updateUploadFileReq,
          originUrl
        }
      };
    } catch (err) {
      const errCode = err instanceof Error ? err.message as ErrorCode : ErrorCode.AWS;
      setManagedErr({
        errCode: convertStringToErrorCode(errCode)
      });
      return null;
    } finally {
      popLoading();
    }
  };

  const uploadSeoFileWithLocalUrl = async (prevData: UpdateSeoReq): Promise<UpdateSeoReq | null> => {
    if (!pageOwner) return null;
    addLoading();
    clearErr();
    const { aui } = pageOwner;
    const localImageUrl = prevData.updateUploadFileReq.originUrl;
    try {
      if (!localImageUrl) return prevData;

      const file = base64ToFileWithMime(localImageUrl);
      if (!sizeCheckBig(file)) {
        throw new Error(ErrorCode.BFE);
      }

      const originUrl = await uploadToS3(file, aui, ServiceType.SEO, []);
      if (originUrl === '') {
        return null;
      }

      return {
        ...prevData,
        updateUploadFileReq: {
          ...prevData.updateUploadFileReq,
          originUrl,
        }
      };
    } catch (err) {
      const errCode = err instanceof Error ? err.message as ErrorCode : ErrorCode.AWS;
      setManagedErr({
        errCode: convertStringToErrorCode(errCode)
      });
      return null;
    } finally {
      popLoading();
    }
  };

  const uploadTextBlockFileWithLocalUrl = async (
    textId: string,
    prevData: UpdateTextBlockReq
  ): Promise<UpdateTextBlockReq | null> => {
    if (!pageOwner) return null;
    addLoading();
    clearErr();
    const { aui } = pageOwner;
    try {
      const localImageUrl = prevData.updateUploadFileReq.originUrl;
      if (!localImageUrl) {
        throw new Error('RVN');
      }
      const file = base64ToFileWithMime(localImageUrl);
      if (!sizeCheckBig(file)) {
        throw new Error(ErrorCode.BFE);
      }

      const originUrl = await uploadToS3(file, aui, ServiceType.TEXT_BLOCK, [textId, prevData.id]);
      if (originUrl === '') {
        return null;
      }

      return {
        ...prevData,
        updateUploadFileReq: {
          ...prevData.updateUploadFileReq,
          originUrl,
        },
      };
    } catch (err) {
      const errCode = err instanceof Error ? err.message as ErrorCode : ErrorCode.AWS;
      setManagedErr({
        errCode: convertStringToErrorCode(errCode)
      });
      return null;
    } finally {
      popLoading();
    }
  };

  const uploadImageHandler = (serviceType: ServiceType, data: UpdateReqType, imageFile?: File) => (
    uploadFileWithLocalUrl(serviceType, data, imageFile)
  );
  const uploadPEImageHandler = <T extends UpdatePEType>(serviceType: ServiceType, data: T, projectId?: string) => uploadFileWithLocalUrlPE(serviceType, data, projectId);
  const uploadSeoImageHandler = (data: UpdateSeoReq) => uploadSeoFileWithLocalUrl(data);
  const uploadTextBlockImageHandler = (textId: string, data: UpdateTextBlockReq) => uploadTextBlockFileWithLocalUrl(textId, data);

  return {
    uploadImage: uploadImageHandler,
    uploadPEImage: uploadPEImageHandler,
    uploadSeoImage: uploadSeoImageHandler,
    uploadTextBlockImage: uploadTextBlockImageHandler
  };
}
